import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion'
import { cn } from '@nt/ui/cn'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'

// Compatibility wrapper: accepts Radix-style type/collapsible props (no-ops in Base UI)
function Accordion({
  className,
  type: _type,
  collapsible: _collapsible,
  ...props
}: AccordionPrimitive.Root.Props & {
  type?: 'single' | 'multiple'
  collapsible?: boolean
}) {
  if (process.env.NODE_ENV !== 'production' && _type === 'single') {
    console.warn('Accordion: type="single" is not supported in Base UI — all panels may open simultaneously.')
  }
  return <AccordionPrimitive.Root data-slot="accordion" className={cn('flex w-full flex-col', className)} {...props} />
}

const accordionItemTone = cva('', {
  variants: {
    tone: {
      default: '',
      signed: 'rounded-control border border-success-border bg-success-tint',
    },
  },
  defaultVariants: { tone: 'default' },
})

function AccordionItem({
  className,
  tone,
  ...props
}: AccordionPrimitive.Item.Props & VariantProps<typeof accordionItemTone>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('not-last:border-b', accordionItemTone({ tone }), className)}
      {...props}
    />
  )
}

const accordionTriggerUnderline = cva('', {
  variants: {
    underline: {
      default: 'hover:underline',
      none: 'hover:no-underline',
    },
  },
  defaultVariants: { underline: 'default' },
})

function AccordionTrigger({
  className,
  children,
  underline,
  ...props
}: AccordionPrimitive.Trigger.Props & VariantProps<typeof accordionTriggerUnderline>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring data-[slot=accordion-trigger-icon]:**:text-muted-foreground rounded-control py-4 text-left text-sm font-medium text-foreground data-[slot=accordion-trigger-icon]:**:ml-auto data-[slot=accordion-trigger-icon]:**:size-4 group/accordion-trigger relative flex flex-1 items-start justify-between border border-transparent transition-all outline-hidden aria-disabled:pointer-events-none aria-disabled:opacity-50',
          accordionTriggerUnderline({ underline }),
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none shrink-0 group-aria-expanded/accordion-trigger:hidden"
        />
        <ChevronUpIcon
          data-slot="accordion-trigger-icon"
          className="pointer-events-none hidden shrink-0 group-aria-expanded/accordion-trigger:inline"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({ className, children, ...props }: AccordionPrimitive.Panel.Props) {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      className="data-open:animate-accordion-down data-closed:animate-accordion-up overflow-hidden text-sm"
      {...props}
    >
      <div
        className={cn(
          'pt-0 pb-4 hover:[&_a]:text-foreground h-(--accordion-panel-height) data-ending-style:h-0 data-starting-style:h-0 [&_a]:underline [&_a]:underline-offset-3 [&_p:not(:last-child)]:mb-4',
          className,
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Panel>
  )
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger }
